import { ClickAway } from "@components/atoms/ClickAway"
import { Logo } from "@components/atoms/Logo"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Themed } from "@components/atoms/Themed"
// dateFormat import removed — date/time display disabled per client feedback
import { cn, tv } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { Link, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { usePageContext } from "../../contexts/PageContext"

export function Header() {
  const mainMenu = useAppData((data) => data.mainMenu?.menuItems?.nodes)
  const mainFeatured = useAppData((data) => data.mainMenu?.mainMenuInfo?.featuredItem)
  const socialMenu = useAppData((data) => data.socialMenu?.menuItems?.nodes)
  // Date/time display removed per client feedback (inaccurate across timezones/VPN)
  const [atTop, setAtTop] = useState(true)
  const [shown, setShown] = useState(true)
  const previousScrollY = useRef(0)
  const { theme, introPlaying } = usePageContext()
  const router = useRouter()
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const ctrl = new AbortController()

    // Using window scroll instead of useWindowScroll to stop rendering whole header every scroll frame
    window.addEventListener(
      "scroll",
      () => {
        const scrollY = document.scrollingElement?.scrollTop || window.pageYOffset
        setAtTop(scrollY < 10)
        if (scrollY > previousScrollY.current && scrollY > 100) {
          setShown(false)
        } else {
          setShown(true)
        }
        previousScrollY.current = scrollY
      },
      {
        signal: ctrl.signal,
      },
    )

    return () => {
      ctrl.abort()
    }
  }, [])

  // Timer removed — date/time display disabled per client feedback

  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(true)
  }, [])

  return (
    <>
      {/* Click-away overlay */}
      <ClickAway dim enabled={menuOpen} onClickAway={() => setMenuOpen(false)} />

      {/* Dropdown menu */}
      <Themed
        theme={introPlaying ? "navy" : menuOpen ? "black" : !!theme && theme !== "none" ? theme : "white"}
        className={cn("fixed top-0 left-0 w-full h-full z-50 themed-fg pointer-events-none")}
      >
        {/* Main menu panel */}
        <div
          className={cn(
            "w-full fixed h-full md:h-auto left-0 top-0 flex flex-col md:flex-row items-center md:items-start justify-center pointer-events-none opacity-0 transition-opacity",
            menuOpen && "opacity-100 pointer-events-auto",
          )}
        >
          {/* Background color of menu panel */}
          <div
            className={cn(
              "absolute left-0 top-0 w-full h-full bg-black text-white rounded-b-lg -translate-y-full transition-transform duration-500",
              menuOpen && "translate-y-0",
            )}
          />
          <div
            className={cn(
              "md:hidden w-full flex flex-col gap-5 px-5 py-7",
              "transition-opacity duration-300 opacity-0",
              menuOpen && "opacity-100 delay-[150ms]",
            )}
          >
            {mainMenu?.map((item) => (
              <Link
                key={item.databaseId}
                href={item.uri}
                onClick={() => setMenuOpen(false)}
                className={cn("type-title-m underline-link data-[active=exact]:underline-link-active")}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div
            className={cn(
              "md:hidden w-full flex flex-col gap-3 px-5 py-7",
              "transition-opacity duration-300 opacity-0",
              menuOpen && "opacity-100 delay-[200ms]",
            )}
          >
            {socialMenu?.map((item) => (
              <Link
                key={item.databaseId}
                href={item.uri}
                onClick={() => setMenuOpen(false)}
                className={cn("type-label-m underline-link data-[active=exact]:underline-link-active")}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* <div
            className={cn(
              "md:hidden flex flex-col gap-2 absolute left-7 bottom-7",
              "transition-opacity duration-300 opacity-0",
              menuOpen && "opacity-100 delay-[200ms]",
            )}
          >
            <p className="type-label-s-caps absolute left-0 -top-4">{nowDate}</p>
            <p className="type-title-s">
              {timeA}
              <span className="blink-pulse">:</span>
              {timeB} AEST
            </p>
          </div> */}

          <div
            className={cn(
              "w-full md:hidden",
              "transition-opacity duration-300 opacity-0 absolute left-5 bottom-7",
              menuOpen && "opacity-100 delay-[250ms]",
            )}
          >
            <div className="flex w-fit relative">
              <div
                contentEditable="plaintext-only"
                id="header-search"
                className="w-fit pr-9 py-3 type-title-s outline-none appearance-none bg-transparent text-current after:content-['Search'] after:absolute after:left-0 after:top-1/2 after:-translate-y-1/2 after:text-current after:text-whiteLightFaded after:pointer-events-none min-w-[6em] [&_br]:hidden [&.edited:after]:hidden"
                onFocus={(e) => {
                  if (e.currentTarget.innerText.trim() === "") {
                    e.currentTarget.classList.add("edited")
                  }
                }}
                onBlur={(e) => {
                  if (e.currentTarget.innerText.trim() === "") {
                    e.currentTarget.classList.remove("edited")
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    const target = e.target as HTMLInputElement
                    const query = target.innerText.trim()
                    setMenuOpen(false)
                    router.navigate(`/search?search=${encodeURIComponent(query)}`)
                  }
                }}
              />
              <svg
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 cursor-pointer"
                onClick={() => {
                  const input = document.querySelector("#header-search") as HTMLInputElement
                  if (input) {
                    const query = input.value
                    setMenuOpen(false)
                    router.navigate(`/search?search=${encodeURIComponent(query)}`)
                  }
                }}
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="1" y="1" width="34" height="34" rx="17" stroke="currentColor" />
                <path
                  d="M24.8008 22.6951L21.8537 19.7481C22.5632 18.8035 22.9462 17.6538 22.9449 16.4725C22.9449 13.455 20.49 11 17.4725 11C14.455 11 12 13.455 12 16.4725C12 19.49 14.455 21.945 17.4725 21.945C18.6538 21.9462 19.8035 21.5632 20.7481 20.8537L23.6951 23.8007C23.8443 23.9341 24.0388 24.0053 24.2389 23.9997C24.4389 23.9941 24.6292 23.9121 24.7706 23.7706C24.9121 23.6292 24.9941 23.4389 24.9997 23.2388C25.0053 23.0388 24.9341 22.8443 24.8008 22.6951ZM13.5635 16.4725C13.5635 15.6994 13.7928 14.9436 14.2223 14.3008C14.6519 13.658 15.2623 13.157 15.9766 12.8611C16.6908 12.5653 17.4768 12.4878 18.2351 12.6387C18.9933 12.7895 19.6898 13.1617 20.2365 13.7084C20.7832 14.2551 21.1555 14.9516 21.3063 15.7099C21.4571 16.4681 21.3797 17.2541 21.0838 17.9683C20.788 18.6826 20.2869 19.2931 19.6441 19.7226C19.0013 20.1522 18.2456 20.3814 17.4725 20.3814C16.4361 20.3801 15.4426 19.9679 14.7098 19.2351C13.977 18.5023 13.5648 17.5088 13.5635 16.4725Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>

          <div
            className={cn(
              "hidden md:grid w-full grid-cols-2 pt-7 pb-8 gap-6 max-w-grid mx-auto mt-headerHeight relative",
            )}
          >
            {mainFeatured && (
              <div
                className={cn(
                  "w-full aspect-[1.22/1] bg-blue text-black rounded-lg p-7",
                  "transition-opacity duration-300 opacity-0",
                  menuOpen && "opacity-100 delay-[250ms]",
                )}
              >
                <Link
                  href={mainFeatured.uri || ""}
                  onClick={() => setMenuOpen(false)}
                  className="w-full h-full flex flex-col justify-between"
                >
                  {mainFeatured.__typename === "Article" && (
                    <div className="flex flex-col gap-7 h-full">
                      <div className="flex flex-col gap-3">
                        <p className="type-label-s-caps shrink-0">FEATURED ARTICLE</p>
                        <p className="type-title-m shrink-0">{mainFeatured.title}</p>
                      </div>
                      <ResponsiveImage
                        {...mainFeatured.pageHeaderArticle?.featuredImages?.[0]!}
                        className="w-full h-full rounded-md overflow-hidden"
                        objectFit="cover"
                      />
                    </div>
                  )}
                  {mainFeatured.__typename === "Podcast" && (
                    <div className="flex flex-col gap-7 h-full">
                      <div className="flex flex-col gap-3">
                        <p className="type-label-s-caps shrink-0">FEATURED PODCAST</p>
                        <p className="type-title-m shrink-0">{mainFeatured.title}</p>
                      </div>
                      <ResponsiveImage
                        {...mainFeatured.pageHeaderPodcast?.featuredImages?.[0]!}
                        className="w-full h-full rounded-md overflow-hidden"
                        objectFit="cover"
                      />
                    </div>
                  )}
                  {mainFeatured.__typename === "WhitePaper" && (
                    <div className="flex flex-col gap-7 h-full">
                      <div className="flex flex-col gap-3">
                        <p className="type-label-s-caps shrink-0">FEATURED WHITEPAPER</p>
                        <p className="type-title-m shrink-0">{mainFeatured.title}</p>
                      </div>
                      <ResponsiveImage
                        {...mainFeatured.pageHeaderWhitepaper?.featuredImages?.[0]!}
                        className="w-full h-full rounded-md overflow-hidden"
                        objectFit="cover"
                      />
                    </div>
                  )}
                  {mainFeatured.__typename === "CaseStudy" && (
                    <div className="flex flex-col gap-7 h-full">
                      <div className="flex flex-col gap-3">
                        <p className="type-label-s-caps shrink-0">FEATURED PROJECT</p>
                        <p className="type-title-m shrink-0">{mainFeatured.title}</p>
                      </div>
                      <ResponsiveImage
                        {...mainFeatured.pageHeaderCaseStudy?.featuredImages?.[0]!}
                        className="w-full h-full rounded-md overflow-hidden"
                        objectFit="cover"
                      />
                    </div>
                  )}
                </Link>
              </div>
            )}
            <div
              className={cn(
                "w-full flex flex-col h-full justify-between gap-5",
                "transition-opacity duration-300 opacity-0",
                menuOpen && "opacity-100 delay-[300ms]",
              )}
            >
              <div className="flex flex-col gap-5">
                <p className="type-label-s-caps">Menu</p>
                <div className="flex flex-col gap-2">
                  {mainMenu?.map((item) => (
                    <Link
                      key={item.databaseId}
                      href={item.uri}
                      onClick={() => setMenuOpen(false)}
                      className={cn("type-title-m underline-link data-[active=exact]:underline-link-active")}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                <div className="flex flex-col gap-1">
                  {socialMenu?.map((item) => (
                    <Link
                      key={item.databaseId}
                      href={item.uri}
                      onClick={() => setMenuOpen(false)}
                      className={cn("type-label-m underline-link data-[active=exact]:underline-link-active")}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div
                className={cn(
                  "w-full",
                  "transition-opacity duration-300 opacity-0",
                  menuOpen && "opacity-100 delay-[350ms]",
                )}
              >
                <div className="flex w-fit relative">
                  <div
                    contentEditable="plaintext-only"
                    id="header-search"
                    className="w-fit pr-8 py-3 type-title-s outline-none appearance-none bg-transparent text-current after:content-['Search'] after:absolute after:left-0 after:top-1/2 after:-translate-y-1/2 after:text-current after:text-whiteLightFaded after:pointer-events-none min-w-[6em] [&_br]:hidden [&.edited:after]:hidden"
                    onFocus={(e) => {
                      if (e.currentTarget.innerText.trim() === "") {
                        e.currentTarget.classList.add("edited")
                      }
                    }}
                    onBlur={(e) => {
                      if (e.currentTarget.innerText.trim() === "") {
                        e.currentTarget.classList.remove("edited")
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        const target = e.target as HTMLInputElement
                        const query = target.innerText.trim()
                        setMenuOpen(false)
                        router.navigate(`/search?search=${encodeURIComponent(query)}`)
                      }
                    }}
                  />
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 cursor-pointer"
                    onClick={() => {
                      const input = document.querySelector("#header-search") as HTMLInputElement
                      if (input) {
                        const query = input.value
                        setMenuOpen(false)
                        router.navigate(`/search?search=${encodeURIComponent(query)}`)
                      }
                    }}
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="34" height="34" rx="17" stroke="currentColor" />
                    <path
                      d="M24.8008 22.6951L21.8537 19.7481C22.5632 18.8035 22.9462 17.6538 22.9449 16.4725C22.9449 13.455 20.49 11 17.4725 11C14.455 11 12 13.455 12 16.4725C12 19.49 14.455 21.945 17.4725 21.945C18.6538 21.9462 19.8035 21.5632 20.7481 20.8537L23.6951 23.8007C23.8443 23.9341 24.0388 24.0053 24.2389 23.9997C24.4389 23.9941 24.6292 23.9121 24.7706 23.7706C24.9121 23.6292 24.9941 23.4389 24.9997 23.2388C25.0053 23.0388 24.9341 22.8443 24.8008 22.6951ZM13.5635 16.4725C13.5635 15.6994 13.7928 14.9436 14.2223 14.3008C14.6519 13.658 15.2623 13.157 15.9766 12.8611C16.6908 12.5653 17.4768 12.4878 18.2351 12.6387C18.9933 12.7895 19.6898 13.1617 20.2365 13.7084C20.7832 14.2551 21.1555 14.9516 21.3063 15.7099C21.4571 16.4681 21.3797 17.2541 21.0838 17.9683C20.788 18.6826 20.2869 19.2931 19.6441 19.7226C19.0013 20.1522 18.2456 20.3814 17.4725 20.3814C16.4361 20.3801 15.4426 19.9679 14.7098 19.2351C13.977 18.5023 13.5648 17.5088 13.5635 16.4725Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <AnimatePresence initial={false}>
          {ready && (shown || menuOpen) && (
            <motion.div
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              className={cn("relative w-full transition-colors duration-300")}
              id="site-header"
              transition={{
                ease: "easeInOut",
                duration: 0.4,
              }}
            >
              <div
                className={cn(
                  "absolute inset-0 top-auto w-full h-[calc(var(--spacing-headerHeight)*2)] rounded-b-lg",
                  "transition-colors duration-300",
                  introPlaying || atTop ? "bg-transparent" : "themed-bg",
                )}
              />
              <div
                className={cn("w-full relative h-headerHeight max-w-grid mx-auto flex items-center justify-between")}
              >
                <Link
                  href="/"
                  onClick={() => {
                    setMenuOpen(false)
                  }}
                  className="pointer-events-auto"
                >
                  <Logo />
                </Link>

                <div className="flex items-end gap-7">
                  <div className="relative type-title-s">
                    <MenuToggle open={menuOpen} setOpen={setMenuOpen} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Themed>
    </>
  )
}

const MenuToggle = ({
  className,
  open,
  setOpen,
}: {
  className?: string
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  const styles = menuToggleStyles({
    open: open,
  })

  return (
    <div onClick={() => setOpen(!open)} className={cn(styles.base(), className)}>
      <div className={cn(styles.line(), styles.line1())} />
      <div className={cn(styles.line(), styles.line2())} />
      <div className={cn(styles.line(), styles.line3())} />
    </div>
  )
}

const menuToggleStyles = tv({
  base: "pointer-events-auto group cursor-pointer flex justify-center items-center shrink-0 size-7 md:size-5 will-change-transform isolate",
  slots: {
    line: "absolute transition-[opacity,transform] duration-150 w-full before:block before:absolute before:top-[-1px] before:transition-none before:bottom-[-1px] before:w-full before:[content:'_'] before:bg-current",
    line1: "",
    line2: "",
    line3: "",
  },
  variants: {
    open: {
      true: {
        line1: "rotate-45",
        line2: "opacity-0",
        line3: "-rotate-45",
      },
      false: {
        line1: "-translate-y-[6px] md:-translate-y-[8px]",
        line2: "",
        line3: "translate-y-[6px] md:translate-y-[8px]",
      },
    },
  },
})
